import React, { ReactNode, useRef, useState } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { usePostHog } from 'posthog-js/react';
import { FilterButton } from './FilterButton';
import { FilterShell } from './FilterShell';

type FilterMenuProps = {
  name: string;
  isActive: boolean;
  summaryLabel: string;
  secondaryLabel?: string;
  onReset: () => void;
  onDialogReset?: () => void;
  onDialogOpen?: () => void;
  onApply?: () => void;
  iconOnly?: boolean;
  children: ReactNode;
};

export const FilterMenu = ({
  name,
  isActive,
  summaryLabel,
  secondaryLabel,
  onReset,
  onDialogReset,
  onDialogOpen,
  onApply,
  iconOnly,
  children,
}: FilterMenuProps) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const posthog = usePostHog();
  const buttonRef = useRef<HTMLDivElement>(null);
  const [desktopOpen, setDesktopOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const openFilter = () => {
    const mode = isSmallScreen ? 'mobile' : 'desktop';
    posthog.capture(`Calendar filter opened`, { mode, name });
    onDialogOpen?.();
    if (mode === 'desktop') {
      setDesktopOpen(true);
    } else {
      setMobileOpen(true);
    }
  };

  const close = () => {
    setDesktopOpen(false);
    setMobileOpen(false);
  };

  const handleApply = onApply
    ? () => {
        onApply();
        close();
      }
    : undefined;

  return (
    <Box ref={buttonRef} sx={{ display: 'inline-flex' }}>
      <FilterButton
        onClick={openFilter}
        onReset={isActive ? onReset : undefined}
        isActive={isActive}
        summaryLabel={summaryLabel}
        secondaryLabel={secondaryLabel}
        isSmallScreen={isSmallScreen}
        iconOnly={iconOnly}
      />
      <FilterShell
        anchorEl={desktopOpen ? buttonRef.current : null}
        open={mobileOpen}
        onClose={close}
        onApply={handleApply}
        onReset={onDialogReset ?? onReset}>
        {children}
      </FilterShell>
    </Box>
  );
};
